<template>
  <div class="product-management-page">
    <div class="header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <div class="title">商品管理</div>
      </div>
    </div>

    <div class="toolbar">
      <div style="display: flex; align-items: center;gap: 10px;">
        <el-input v-model="keyword" placeholder="输入商品名称/ID/文案/失效原因 或 佣金区间如 1-5" clearable class="search-input"
          @keyup.enter.native="handleSearch" />
        <div style="display: flex; align-items: center;gap: 10px; flex: 1;">
          <el-select v-model="invalidStatusFilter" placeholder="失效状态" clearable class="status-select"
            @change="handleSearch" style="width: 150px;margin-bottom: 10px;">
            <el-option label="全部" value=""></el-option>
            <el-option label="未失效" value="NONE"></el-option>
            <el-option label="已上报" value="REPORTED"></el-option>
            <el-option label="已确认失效" value="CONFIRMED"></el-option>
            <el-option label="已驳回" value="REJECTED"></el-option>
          </el-select>
          <el-select v-model="cid1Filter" placeholder="类目" clearable class="category-select" @change="handleSearch"
            style="width: 150px;margin-bottom: 10px;">
            <el-option v-for="category in cid3Options" :key="category.value" :label="category.label"
              :value="category.value" />
          </el-select>
        </div>
      </div>
      <div class="search-filters">
        <el-button class="btn" type="primary" @click="handleBatchCreate">
          新增
        </el-button>
        <el-button class="btn" type="danger" @click="handleBatchDelete" :disabled="!multipleSelection.length">
          删除
        </el-button>
        <el-button class="btn" type="primary" @click="handleBatchSetInvalidStatus('NONE')"
          :disabled="!multipleSelection.length">
          激活
        </el-button>
        <el-button class="btn" type="primary" @click="handleBatchSetInvalidStatus('CONFIRMED')"
          :disabled="!multipleSelection.length">
          失效
        </el-button>
        <el-button class="btn" type="primary" @click="handleSyncJson">
          同步
        </el-button>
        <el-button class="btn" type="primary" @click="handleBatchReplace" :disabled="!multipleSelection.length">
          替换
        </el-button>
        <el-button class="btn" type="primary" @click="handleSearch">
          搜索
        </el-button>
      </div>
    </div>

    <div class="content-table-container">
      <el-table ref="productTable" :data="products" border stripe v-loading="loading" empty-text="暂无商品数据"
        @selection-change="handleSelectionChange" @row-click="handleRowClick" height="calc(100vh - 260px)">
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column label="商品ID" width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>{{ scope.row.itemId }}</div>
            <div>金额：￥{{ scope.row.commission }}</div>
            <div>比例：{{ scope.row.commissionShare }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="skuName" label="商品名称" min-width="200" show-overflow-tooltip />
        <el-table-column prop="guideText" label="指导文案" min-width="220">
          <template slot-scope="scope">
            <div class="guide-text-cell" v-html="scope.row.guideText"></div>
          </template>
        </el-table-column>
        <el-table-column prop="shopName" label="店铺" width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>{{ scope.row.shopName || '--' }}</div>
            <div class="sub-text" v-if="scope.row.shopLevel">等级：{{ scope.row.shopLevel }}</div>
          </template>
        </el-table-column>
        <el-table-column label="类目" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.cid1Name || '-' }} / {{ scope.row.cid2Name || '-' }} / {{ scope.row.cid3Name || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="guidePrice" label="指导价" width="110">
          <template slot-scope="scope">
            ￥{{ scope.row.guidePrice }}
          </template>
        </el-table-column>
        <el-table-column label="服务评分" min-width="220">
          <template slot-scope="scope">
            <div>物流：{{ scope.row.logisticsLvyueScore }}</div>
            <div>售后：{{ scope.row.afterServiceScore }}</div>
            <div>好评率：{{ scope.row.goodCommentsShare }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="comments" label="评价数" width="110">
          <template slot-scope="scope">
            {{ scope.row.comments ?? '--' }}
          </template>
        </el-table-column>
        <el-table-column label="失效状态" width="150">
          <template slot-scope="scope">
            <el-tag :type="getInvalidStatusType(scope.row.invalidStatus)" size="mini">
              {{ getInvalidStatusText(scope.row.invalidStatus) }}
            </el-tag>
            <div v-if="scope.row.reportedBy" class="sub-text" style="font-size: 12px; color: #999; margin-top: 4px;">
              上报人：{{ scope.row.reportedBy }}
            </div>
            <div v-if="scope.row.reportedTime" class="sub-text" style="font-size: 12px; color: #999;">
              {{ formatDate(scope.row.reportedTime) }}
            </div>
            <div v-if="scope.row.invalidReason" class="sub-text"
              style="font-size: 12px; color: #999; margin-top: 4px; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
              :title="scope.row.invalidReason">
              原因：{{ scope.row.invalidReason }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="updatedTime" label="更新时间" width="160" show-overflow-tooltip />
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="primary" @click="copyLink(scope.row)">复制链接</el-button>
            <el-button v-if="scope.row.invalidStatus === 'REPORTED'" size="mini" type="primary" style="color:#E6A23C"
              @click="handleConfirmInvalid(scope.row)">
              确认失效
            </el-button>
            <el-button v-if="scope.row.invalidStatus === 'REPORTED'" size="mini" type="primary" style="color:#409EFF"
              @click="handleRejectInvalid(scope.row)">
              驳回
            </el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pagination-container" v-if="pagination.total > 0">
      <el-pagination background layout="total, sizes, prev, pager, next" :page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100, 200]" :current-page="pagination.currentPage" :total="pagination.total"
        @current-change="handlePageChange" @size-change="handlePageSizeChange" />
    </div>

    <!-- 批量新增对话框 -->
    <el-dialog title="批量新增商品" :visible.sync="batchDialogVisible" width="600px" destroy-on-close>
      <!-- 输入模式选择 -->
      <div style="margin-bottom: 15px;">
        <el-radio-group v-model="batchInputMode" @change="handleInputModeChange">
          <el-radio label="table">表格输入</el-radio>
          <el-radio label="json">JSON输入</el-radio>
        </el-radio-group>
        <span style="margin-left: 20px; color: #909399; font-size: 12px;">提示：一次最多支持查询20个itemId</span>
      </div>

      <!-- 表格输入模式 -->
      <div v-if="batchInputMode === 'table'">
        <div style="margin-bottom: 10px;">
          <el-button size="small" @click="addBatchRow">添加一行</el-button>
          <el-button size="small" @click="removeBatchRow">删除一行</el-button>
        </div>
        <el-table :data="batchForm.items" border stripe max-height="500">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column label="商品ID (itemId)" min-width="200">
            <template slot-scope="scope">
              <el-input v-model="scope.row.itemId" placeholder="请输入商品ID" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="指导文案 (guideText)" min-width="400">
            <template slot-scope="scope">
              <div class="table-quill-wrapper">
                <quill-editor v-model="scope.row.guideText" :options="editorOption"
                  class="content-quill-editor table-quill-editor" />
              </div>
            </template>
          </el-table-column>
          <el-table-column label="指导价 (guidePrice)" width="150">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.guidePrice" :min="0" :step="0.01" :precision="2"
                controls-position="right" size="small" style="width: 100%;" />
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- JSON输入模式 -->
      <div v-if="batchInputMode === 'json'">
        <el-alert title="JSON格式示例：" type="info" :closable="false" style="margin-bottom: 10px;">
          <pre style="margin: 5px 0; font-size: 12px;">[
        {
        "itemId": "商品ID1",
        "guideText": "指导文案1",
        "guidePrice": 100.00
        },
        {
        "itemId": "商品ID2",
        "guideText": "指导文案2",
        "guidePrice": 200.00
        }
        ]</pre>
        </el-alert>
        <el-input v-model="batchJsonInput" type="textarea" :rows="15" placeholder="请输入JSON格式的商品数据"
          style="font-family: 'Courier New', monospace;" />
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="batchSubmitLoading" @click="submitBatchForm">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 同步JSON对话框 -->
    <el-dialog title="同步JSON数据" :visible.sync="syncJsonDialogVisible" width="600px" destroy-on-close>
      <el-form :model="syncJsonForm" label-width="120px">
        <el-form-item label="selfCid">
          <el-input v-model="syncJsonForm.selfCid" placeholder="请输入selfCid（默认：0）" />
        </el-form-item>
        <el-form-item label="brandCode">
          <el-input v-model="syncJsonForm.brandCode" placeholder="请输入brandCode（默认：0）" />
        </el-form-item>
        <el-form-item label="isSubsidy">
          <el-input v-model="syncJsonForm.isSubsidy" placeholder="请输入isSubsidy（默认：0）" />
        </el-form-item>
        <el-form-item label="pageNum">
          <el-input-number v-model="syncJsonForm.pageNum" :min="1" placeholder="页码（默认：1）" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="pageSize">
          <el-input-number v-model="syncJsonForm.pageSize" :min="1" :max="10000" placeholder="每页数量（默认：10）"
            style="width: 100%;" />
        </el-form-item>
        <el-form-item label="code">
          <el-input v-model="syncJsonForm.code" placeholder="请输入code" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="syncJsonDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="syncJsonLoading" @click="submitSyncJson">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 预览对话框 -->
    <el-dialog title="同步结果预览" :visible.sync="previewDialogVisible" width="600px" destroy-on-close>
      <div style="margin-bottom: 15px;">
        <el-input v-model="previewSearchKeyword" placeholder="输入关键词，匹配的项会标红并排在前面" clearable>
          <template slot="prepend">搜索</template>
        </el-input>
      </div>
      <el-table :data="filteredPreviewItems" border max-height="500" @selection-change="handlePreviewSelectionChange">
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column prop="itemId" label="商品ID" width="200" show-overflow-tooltip />
        <el-table-column label="指导文案" min-width="300">
          <template slot-scope="scope">
            <el-input type="textarea" :rows="3" v-model="scope.row.guideText" placeholder="可在此修改指导文案"
              :class="{ 'matched-text': scope.row.isMatched }" />
          </template>
        </el-table-column>
        <el-table-column label="指导价" width="150">
          <template slot-scope="scope">
            <el-input-number v-model="scope.row.guidePrice" :min="0" :step="1" :precision="2" controls-position="right"
              style="width: 100%;" />
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="previewDialogVisible = false; previewSearchKeyword = ''">取 消</el-button>
        <el-button type="primary" @click="confirmPreview">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 批量替换对话框 -->
    <el-dialog title="批量替换指导文案" :visible.sync="replaceDialogVisible" width="500px" destroy-on-close>
      <el-form :model="replaceForm" label-width="100px">
        <el-form-item label="替换前">
          <el-input v-model="replaceForm.beforeText" type="textarea" :rows="3" placeholder="请输入要替换的文本内容" />
        </el-form-item>
        <el-form-item label="替换后">
          <el-input v-model="replaceForm.afterText" type="textarea" :rows="3" placeholder="请输入替换后的文本内容" />
        </el-form-item>
        <el-form-item label="是否激活">
          <el-switch v-model="replaceForm.isActive" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="replaceDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="replaceLoading" @click="submitReplace">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑对话框 -->
    <el-dialog title="编辑商品" :visible.sync="dialogVisible" width="600px" destroy-on-close>
      <el-form :model="form" label-width="120px" :rules="rules" ref="productForm">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="商品ID" prop="itemId">
              <el-input v-model="form.itemId" :disabled="true" placeholder="商品ID" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商品名称" prop="skuName">
              <el-input v-model="form.skuName" :disabled="true" placeholder="商品名称" />
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 编辑时提示可编辑字段 -->
        <el-alert title="编辑模式：可调整指导信息和失效状态" type="info" :closable="false" style="margin-bottom: 20px;" />
        <el-form-item label="指导文案 (guideText)" prop="guideText">
          <quill-editor v-model="form.guideText" :options="editorOption" class="content-quill-editor"
            style="min-height: 200px;" />
        </el-form-item>
        <el-form-item label="指导价 (guidePrice)" prop="guidePrice">
          <el-input-number v-model="form.guidePrice" :min="0" :step="0.01" :precision="2" controls-position="right"
            style="width: 100%;" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="失效状态" prop="invalidStatus">
              <el-select v-model="form.invalidStatus" placeholder="请选择失效状态" clearable>
                <el-option v-for="option in invalidStatusOptions" :key="option.value" :label="option.label"
                  :value="option.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="失效原因" prop="invalidReason">
              <el-input v-model="form.invalidReason" placeholder="请输入失效原因" maxlength="100" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">保 存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getProducts, saveProduct, deleteProduct, batchDeleteProducts, batchUpdateProductInvalidStatus, batchQueryAndSaveProducts, syncSkuList, handleProductInvalidReportByItemId, getCid3Categories, batchReplaceGuideText } from '@/utils/jdApi'
import { copyToClipboardWithMessage } from '@/utils/clipboard'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

export default {
  name: 'ProductManagement',
  components: {
    quillEditor
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      batchSubmitLoading: false,
      keyword: '',
      invalidStatusFilter: '',
      cid1Filter: '',
      cid3Options: [
        { label: '全部', value: '' },
      ],
      products: [],
      pagination: {
        currentPage: 1,
        pageSize: 200,
        total: 0
      },
      dialogVisible: false,
      batchDialogVisible: false,
      multipleSelection: [],
      syncJsonDialogVisible: false,
      previewDialogVisible: false,
      previewItems: [],
      previewSelection: [],
      previewSearchKeyword: '', // 预览搜索关键词
      syncJsonLoading: false,
      replaceDialogVisible: false,
      replaceLoading: false,
      replaceForm: {
        beforeText: '',
        afterText: '',
        isActive: false
      },
      syncJsonForm: {
        selfCid: '0',
        brandCode: '0',
        isSubsidy: '0',
        pageNum: 1,
        pageSize: 10,
        code: ''
      },
      batchInputMode: 'table', // 'table' 或 'json'
      batchJsonInput: '',
      isEdit: false,
      form: this.createEmptyForm(),
      invalidStatusOptions: [
        { label: '未失效', value: 'NONE' },
        { label: '已上报', value: 'REPORTED' },
        { label: '已确认失效', value: 'CONFIRMED' },
        { label: '已驳回', value: 'REJECTED' }
      ],
      batchForm: {
        items: [{ itemId: '', guideText: '', guidePrice: null }]
      },
      rules: {
        guideText: [{ required: false, message: '请输入指导文案', trigger: 'blur' }],
        guidePrice: [{ required: false, message: '请输入指导价', trigger: 'blur' }],
        invalidStatus: [{ required: false }],
        invalidReason: [{ required: false }]
      },
      // 富文本编辑器配置（与枚举值管理保持一致）
      editorOption: {
        placeholder: '请输入指导文案...（支持换行、emoji等）',
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ header: [1, 2, 3, false] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['link', 'image'],
            ['clean']
          ]
        }
      }
    }
  },
  computed: {
    // 过滤和排序预览项：包含关键词的排在前面，并标记为匹配
    filteredPreviewItems() {
      if (!this.previewSearchKeyword || !this.previewSearchKeyword.trim()) {
        return this.previewItems.map(item => ({ ...item, isMatched: false }))
      }
      const keyword = this.previewSearchKeyword.trim().toLowerCase()
      const matched = []
      const unmatched = []

      this.previewItems.forEach(item => {
        const guideText = (item.guideText || '').toLowerCase()
        const isMatched = guideText.includes(keyword)
        const itemWithFlag = { ...item, isMatched }

        if (isMatched) {
          matched.push(itemWithFlag)
        } else {
          unmatched.push(itemWithFlag)
        }
      })

      // 匹配的排在前面
      return [...matched, ...unmatched]
    }
  },
  created() {
    this.loadCategories() // 加载类目列表
    this.fetchProducts()
  },
  methods: {
    goBack() {
      this.$router.push('/mypage')
    },
    // 加载类目列表
    loadCategories() {
      getCid3Categories()
        .then(res => {
          if (res && res.success && Array.isArray(res.data)) {
            this.cid3Options = [
              { label: '全部', value: '' },
              ...res.data
                .filter(item => item.cid3Name && item.cid3Name.trim())
                .map(item => ({
                  label: item.cid3Name,
                  value: item.cid3Name
                })).sort((a, b) => a.label.localeCompare(b.label))
            ]
          }
        })
        .catch(error => {
          console.error('加载类目列表失败:', error)
        })
    },
    createEmptyForm() {
      return {
        itemId: '',
        skuName: '',
        shopName: '',
        shopLevel: null,
        cid1: null,
        cid1Name: '',
        cid2: null,
        cid2Name: '',
        cid3: null,
        cid3Name: '',
        commission: null,
        commissionShare: null,
        couponCommission: null,
        plusCommissionShare: null,
        startTime: null,
        endTime: null,
        isLock: false,
        imageList: '',
        spuid: '',
        discount: null,
        link: '',
        guideText: '',
        guidePrice: null,
        logisticsLvyueScore: null,
        afterServiceScore: null,
        comments: null,
        goodCommentsShare: null,
        invalidStatus: 'NONE',
        invalidReason: ''
      }
    },
    fetchProducts(page = 0) {
      this.loading = true
      const params = {
        page,
        size: this.pagination.pageSize,
        keyword: this.keyword || null,
        excludeConfirmed: false // 商品管理页面不过滤已确认失效的商品
      }
      // 如果选择了失效状态过滤，添加参数
      if (this.invalidStatusFilter) {
        params.invalidStatus = this.invalidStatusFilter
      }
      // 如果选择了类目过滤，添加参数
      if (this.cid1Filter) {
        params.cid1 = this.cid1Filter
      }
      getProducts(params)
        .then(res => {
          this.products = res?.data || []
          this.pagination.total = res?.totalElements ?? this.products.length
          this.pagination.currentPage = (res?.page ?? page) + 1
        }, error => {
          this.$message.error('加载商品失败：' + (error.message || '请稍后重试'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchProducts(0)
    },
    handlePageChange(page) {
      this.fetchProducts(page - 1)
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.fetchProducts(0)
    },
    handleSelectionChange(selection) {
      this.multipleSelection = selection || []
    },
    handleRowClick(row, column, event) {
      // 如果点击的是按钮、链接或其他交互元素，不触发选中
      const target = event.target
      if (target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('.el-button') ||
        target.closest('.el-tag')) {
        return
      }
      // 切换选中状态
      this.$refs.productTable.toggleRowSelection(row)
    },
    handleBatchCreate() {
      this.batchInputMode = 'table'
      this.batchForm.items = [{ itemId: '', guideText: '', guidePrice: null }]
      this.batchJsonInput = ''
      this.batchDialogVisible = true
    },
    handleSyncJson() {
      this.syncJsonDialogVisible = true
    },
    submitSyncJson() {
      // 验证必填参数
      if (!this.syncJsonForm.code || !this.syncJsonForm.code.trim()) {
        this.$message.warning('code 参数不能为空')
        return
      }
      this.syncJsonLoading = true
      // 构建请求参数
      const params = {
        selfCid: this.syncJsonForm.selfCid || '0',
        brandCode: this.syncJsonForm.brandCode || '0',
        isSubsidy: this.syncJsonForm.isSubsidy || '0',
        pageNum: this.syncJsonForm.pageNum || 1,
        pageSize: this.syncJsonForm.pageSize || 10,
        code: this.syncJsonForm.code.trim()
      }
      syncSkuList(params)
        .then(response => {
          if (response.success && response.data && response.data.code === 200 && response.data.rows && response.data.rows.length > 0) {
            const items = response.data.rows.map(row => ({
              itemId: row.itemId || '',
              guideText: this.sanitizeGuideText(row.guideText || ''),
              guidePrice: row.guidePrice ? parseFloat(row.guidePrice) : null
            })).filter(item => item.itemId)

            if (items.length === 0) {
              this.$message.warning('返回的数据中没有有效的商品ID')
              return
            }
            this.previewItems = items
            this.previewDialogVisible = true
          } else {
            this.$message.warning('接口返回数据为空或格式错误')
          }
        }, () => {
          this.$message.error('同步失败')
        })
        .finally(() => {
          this.syncJsonLoading = false
        })
    },
    async confirmPreview() {
      // 使用过滤后的数据
      const targetItems = this.previewSelection.length ? this.previewSelection : this.filteredPreviewItems

      if (!targetItems.length) {
        this.$message.warning('没有可保存的商品')
        return
      }

      const chunkSize = 20
      let totalSaved = 0
      let failedCount = 0

      for (let start = 0; start < targetItems.length; start += chunkSize) {
        const chunk = targetItems.slice(start, start + chunkSize)
        try {
          const res = await batchQueryAndSaveProducts(chunk)
          if (res && res.success) {
            totalSaved += res.count || chunk.length
          } else {
            failedCount += chunk.length
            this.$message.warning(res?.message || `第 ${start / chunkSize + 1} 组保存失败，已跳过`)
          }
        } catch (error) {
          failedCount += chunk.length
          this.$message.error('批量保存失败，已跳过该组：' + (error.response?.data?.message || error.message))
        }
      }

      if (totalSaved > 0) {
        this.$message.success(`成功同步并保存 ${totalSaved} 个商品${failedCount ? `，失败 ${failedCount} 个` : ''}`)
      } else {
        this.$message.warning('未成功保存任何商品')
      }

      this.previewDialogVisible = false
      this.previewSelection = []
      this.previewSearchKeyword = ''
      this.syncJsonDialogVisible = false
      this.fetchProducts(this.pagination.currentPage - 1)
    },
    handlePreviewSelectionChange(selection) {
      this.previewSelection = selection || []
    },
    handleInputModeChange() {
      if (this.batchInputMode === 'table' && this.batchForm.items.length === 0) {
        this.batchForm.items = [{ itemId: '', guideText: '', guidePrice: null }]
      }
    },
    addBatchRow() {
      if (this.batchForm.items.length >= 20) {
        this.$message.warning('一次最多支持查询20个itemId')
        return
      }
      this.batchForm.items.push({ itemId: '', guideText: '', guidePrice: null })
    },
    removeBatchRow() {
      if (this.batchForm.items.length > 1) {
        this.batchForm.items.pop()
      } else {
        this.$message.warning('至少保留一行')
      }
    },
    submitBatchForm() {
      let validItems = []

      if (this.batchInputMode === 'table') {
        validItems = this.batchForm.items
          .filter(item => item.itemId && item.itemId.trim())
          .map(item => ({
            ...item,
            guideText: this.sanitizeGuideText(item.guideText)
          }))
      } else {
        // JSON模式：解析JSON输入
        try {
          const parsed = JSON.parse(this.batchJsonInput)
          if (!Array.isArray(parsed)) {
            this.$message.error('JSON格式错误：必须是数组格式')
            return
          }
          validItems = parsed
            .filter(item => item.itemId && item.itemId.trim())
            .map(item => ({
              ...item,
              guideText: this.sanitizeGuideText(item.guideText)
            }))
        } catch (error) {
          this.$message.error('JSON格式错误：' + error.message)
          return
        }
      }

      // 验证数据
      if (validItems.length === 0) {
        this.$message.warning('请至少输入一个商品ID')
        return
      }

      if (validItems.length > 20) {
        this.$message.warning('一次最多支持查询20个itemId')
        return
      }

      this.batchSubmitLoading = true
      batchQueryAndSaveProducts(validItems)
        .then(res => {
          if (res.success) {
            this.$message.success(`成功保存 ${res.count} 个商品`)
            this.batchDialogVisible = false
            this.fetchProducts(this.pagination.currentPage - 1)
          } else {
            this.$message.error(res.message || '保存失败')
          }
        }, error => {
          this.$message.error('保存失败：' + (error.response?.data?.message || error.message))
        })
        .finally(() => {
          this.batchSubmitLoading = false
        })
    },
    handleEdit(row) {
      this.isEdit = true
      this.form = {
        ...this.createEmptyForm(),
        ...JSON.parse(JSON.stringify(row))
      }
      this.dialogVisible = true
    },
    submitForm() {
      this.$refs.productForm.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        const updateData = {
          ...JSON.parse(JSON.stringify(this.form)),
          guideText: this.form.guideText,
          guidePrice: this.form.guidePrice,
          invalidStatus: this.form.invalidStatus,
          invalidReason: this.form.invalidReason
        }
        saveProduct(updateData)
          .then(() => {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.fetchProducts(this.pagination.currentPage - 1)
          }, error => {
            this.$message.error('保存失败：' + (error.response?.data?.message || error.message))
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
    },
    handleDelete(row) {
      this.$confirm(`确认删除【${row.skuName}】吗？`, '提示', { type: 'warning' })
        .then(() => {
          deleteProduct(row.id)
            .then(() => {
              this.$message.success('删除成功')
              this.fetchProducts(this.pagination.currentPage - 1)
            }, error => {
              this.$message.error('删除失败：' + (error.response?.data?.message || error.message))
            })
        })
        .catch(() => {
          // 用户取消删除，避免 Uncaught (in promise) cancel
        })
    },
    handleBatchDelete() {
      if (!this.multipleSelection.length) {
        this.$message.warning('请先选择要删除的商品')
        return
      }
      this.$confirm(`确认删除选中的 ${this.multipleSelection.length} 个商品？`, '提示', { type: 'warning' })
        .then(() => {
          const ids = this.multipleSelection.map(i => i.id)
          batchDeleteProducts(ids)
            .then(res => {
              if (res.success) {
                this.$message.success(`成功删除 ${res.count || ids.length} 个商品`)
                this.fetchProducts(this.pagination.currentPage - 1)
              } else {
                this.$message.error(res.message || '批量删除失败')
              }
            })
            .catch(error => {
              this.$message.error('批量删除失败：' + (error.response?.data?.message || error.message))
            })
        })
        .catch(() => {
          // 用户取消批量删除
        })
    },
    handleBatchSetInvalidStatus(status) {
      if (!this.multipleSelection.length) {
        this.$message.warning('请先选择要操作的商品')
        return
      }
      const text = status === 'NONE' ? '激活' : '设置为失效'
      this.$confirm(`确认将选中的 ${this.multipleSelection.length} 个商品批量${text}吗？`, '提示', { type: 'warning' })
        .then(() => {
          const ids = this.multipleSelection.map(i => i.id)
          batchUpdateProductInvalidStatus(ids, status)
            .then(res => {
              if (res && res.success) {
                this.$message.success(`批量${text}成功，已更新 ${res.count || ids.length} 个商品`)
                this.fetchProducts(this.pagination.currentPage - 1)
              } else {
                this.$message.error(res?.message || `批量${text}失败`)
              }
            })
            .catch(error => {
              this.$message.error(`批量${text}失败：` + (error.response?.data?.message || error.message))
            })
        })
        .catch(() => {
          // 用户取消操作
        })
    },
    copyLink(row) {
      if (!row.link) {
        this.$message.warning('暂无推广链接')
        return
      }
      copyToClipboardWithMessage(row.link, this, '链接已复制', '复制失败')
    },
    sanitizeGuideText(text) {
      if (!text) return ''
      let sanitized = text
      sanitized = sanitized.replace(/(?:🛍)?下单[:：]\s*https?:\/\/[^\s<]+/giu, '')
      sanitized = sanitized.replace(/\n{3,}/g, '\n\n')
      sanitized = sanitized.replace(/计算公式[:：]/g, '到手价：')
      return sanitized.trim()
    },
    // 获取失效状态标签类型
    getInvalidStatusType(status) {
      const statusMap = {
        'NONE': 'info',
        'REPORTED': 'warning',
        'CONFIRMED': 'danger',
        'REJECTED': 'success'
      }
      return statusMap[status] || 'info'
    },
    // 获取失效状态文本
    getInvalidStatusText(status) {
      const statusMap = {
        'NONE': '未上报',
        'REPORTED': '已上报',
        'CONFIRMED': '已确认失效',
        'REJECTED': '已驳回'
      }
      return statusMap[status] || '未知'
    },
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    // 确认失效
    handleConfirmInvalid(row) {
      this.$prompt('请输入确认原因（可选）', '确认失效', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPlaceholder: '请输入确认原因...'
      }).then(({ value }) => {
        handleProductInvalidReportByItemId(row.itemId, 'CONFIRMED', value || '管理员确认失效')
          .then(response => {
            if (response && response.success) {
              this.$message.success('确认失效成功')
              this.fetchProducts(this.pagination.currentPage - 1)
            } else {
              this.$message.error(response?.message || '确认失效失败')
            }
          }).catch(error => {
            console.error('确认失效失败:', error)
            this.$message.error('确认失效失败: ' + (error.response?.data?.message || error.message || '未知错误'))
          })
      }).catch(() => {
        // 用户取消
      })
    },
    // 驳回失效上报
    handleRejectInvalid(row) {
      this.$prompt('请输入驳回原因（可选）', '驳回失效上报', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPlaceholder: '请输入驳回原因...'
      }).then(({ value }) => {
        handleProductInvalidReportByItemId(row.itemId, 'REJECTED', value || '管理员驳回')
          .then(response => {
            if (response && response.success) {
              this.$message.success('驳回成功')
              this.fetchProducts(this.pagination.currentPage - 1)
            } else {
              this.$message.error(response?.message || '驳回失败')
            }
          }).catch(error => {
            console.error('驳回失败:', error)
            this.$message.error('驳回失败: ' + (error.response?.data?.message || error.message || '未知错误'))
          })
      }).catch(() => {
        // 用户取消
      })
    },
    // 批量替换
    handleBatchReplace() {
      if (!this.multipleSelection.length) {
        this.$message.warning('请先选择要替换的商品')
        return
      }
      this.replaceForm.beforeText = ''
      this.replaceForm.afterText = ''
      this.replaceDialogVisible = true
    },
    // 提交替换
    async submitReplace() {
      if (!this.replaceForm.beforeText || !this.replaceForm.beforeText.trim()) {
        this.$message.warning('请输入要替换的文本内容')
        return
      }
      const beforeText = this.replaceForm.beforeText.trim()
      const afterText = this.replaceForm.afterText || ''
      this.replaceLoading = true
      try {
        // 获取选中商品的ID列表
        const ids = this.multipleSelection.map(product => product.id)

        // 调用后端批量替换接口
        const res = await batchReplaceGuideText(ids, beforeText, afterText, this.replaceForm.isActive)

        if (res && res.success) {
          const successCount = res.successCount || 0
          const skipCount = res.skipCount || 0
          const failCount = res.failCount || 0

          let message = `成功替换 ${successCount} 个商品的指导文案`
          if (skipCount > 0) {
            message += `，${skipCount} 个商品未包含要替换的文本`
          }
          if (failCount > 0) {
            message += `，${failCount} 个失败`
          }
          this.$message.success(message)
          this.replaceDialogVisible = false
          this.fetchProducts(this.pagination.currentPage - 1)
        } else {
          this.$message.error(res?.message || '批量替换失败')
        }
      } catch (error) {
        this.$message.error('批量替换失败')
      } finally {
        this.replaceLoading = false
      }
    }
  }
};
</script>

<style scoped lang="scss">
.product-management-page {
  margin: 0 auto;
  padding: 10px;
  background: #f8f9fa;
  min-height: 100vh;
  box-sizing: border-box;
}

.header {
  background: linear-gradient(135deg, #42b983, #369870);
  color: #fff;
  padding: 4px;
  border-radius: 12px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.toolbar {
  background: #fff;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 10px;
}

.search-filters {
  display: flex;
  /* flex-wrap: wrap; */
  gap: 10px;
  align-items: center;
}

.search-input {
  flex: 1;
  margin-bottom: 10px;
}

.btn {
  border: none;
  border-radius: 6px;
  padding: 8px 0px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  flex: 1;
  margin-left: 0px !important;
}

.content-table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 12px;
  overflow-x: auto;
  /* height: calc(100vh - 270px); */
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 12px;
  margin-top: 10px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.guide-text-cell {
  max-height: 180px;
  overflow-y: auto;
  white-space: pre-wrap;
}

@media (max-width: 768px) {
  .search-filters {
    align-items: stretch;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .content-table-container .el-table {
    min-width: 560px;
  }

  .pagination-container {
    padding: 10px;
  }

  .guide-text-cell {
    max-height: 120px;
  }
}

.search-input :deep(.el-input__inner) {
  border-radius: 6px;
  height: 40px;
}

/* 预览对话框中匹配的文本样式 */
.matched-text :deep(.el-textarea__inner) {
  color: #f56c6c;
  font-weight: 500;
}

/* 富文本编辑器样式 */
.content-quill-editor {
  width: 100%;
}

.content-quill-editor :deep(.ql-toolbar) {
  border: 1px solid #dcdfe6;
  border-radius: 4px 4px 0 0;
  background: #fafafa;
}

.content-quill-editor :deep(.ql-container) {
  border: 1px solid #dcdfe6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  font-size: 14px;
}

.content-quill-editor :deep(.ql-editor) {
  min-height: 100px;
  max-height: 200px;
  overflow-y: auto;
}

.content-quill-editor :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #c0c4cc;
}

/* 表格中的富文本编辑器样式 */
.table-quill-wrapper {
  width: 100%;
  padding: 5px 0;
}

.table-quill-editor :deep(.ql-toolbar) {
  padding: 4px;
  border: 1px solid #dcdfe6;
  border-radius: 4px 4px 0 0;
  background: #fafafa;
}

.table-quill-editor :deep(.ql-toolbar .ql-formats) {
  margin-right: 8px;
}

.table-quill-editor :deep(.ql-container) {
  border: 1px solid #dcdfe6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  font-size: 13px;
  height: 120px;
}

.table-quill-editor :deep(.ql-editor) {
  min-height: 100px;
  max-height: 120px;
  overflow-y: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
}

.table-quill-editor :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #c0c4cc;
  font-size: 13px;
}

/* 指导文案展示样式 - 使用 pre 标签保留格式 */
.guide-text-display-wrapper {
  width: 100%;
  padding: 5px 0;
}

.guide-text-display {
  margin: 0;
  padding: 8px;
  font-family: 'Courier New', 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  color: #333;
}
</style>
